import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import type { UserRole } from '@/types';
import { cn } from '@/lib/utils';

interface NavItem {
  label: string;
  href: string;
  roles: UserRole[];
}

const navigationItems: NavItem[] = [
  {
    label: 'Dashboard',
    href: '/',
    roles: [
      'sales_ae',
      'service_ae',
      'sales_support',
      'sales_vp',
      'gvp',
      'rvp',
      'exceptions_member',
      'adops_leadership',
      'admin',
    ],
  },
  {
    label: 'My Requests',
    href: '/holds',
    roles: ['sales_ae', 'service_ae', 'sales_support', 'sales_vp', 'gvp', 'rvp', 'admin'],
  },
  {
    label: 'Charting Queue',
    href: '/charting',
    roles: ['charting_member', 'charting_manager'],
  },
  {
    label: 'Exceptions',
    href: '/exceptions',
    roles: ['exceptions_member', 'admin', 'sales_vp', 'gvp', 'rvp', 'adops_leadership'],
  },
  {
    label: 'User Management',
    href: '/admin/users',
    roles: ['admin', 'adops_leadership'],
  },
  {
    label: 'Inventory',
    href: '/admin/inventory',
    roles: ['admin', 'adops_leadership'],
  },
  {
    label: 'Product Catalog',
    href: '/admin/product-catalog',
    roles: ['admin', 'adops_leadership'],
  },
  {
    label: 'Admin Tools',
    href: '/admin/tools',
    roles: ['admin', 'adops_leadership'],
  },
  {
    label: 'Reports',
    href: '/reports',
    roles: ['sales_vp', 'gvp', 'rvp', 'charting_manager', 'adops_leadership'],
  },
  {
    label: 'Analytics',
    href: '/analytics',
    roles: ['adops_leadership', 'charting_manager', 'admin', 'sales_vp', 'gvp', 'rvp'],
  },
  {
    label: 'Notifications',
    href: '/notifications',
    roles: [
      'sales_ae',
      'service_ae',
      'sales_support',
      'sales_vp',
      'gvp',
      'rvp',
      'charting_member',
      'charting_manager',
      'exceptions_member',
      'adops_leadership',
      'admin',
    ],
  },
];

const salesLandingRoles: UserRole[] = ['sales_ae', 'sales_support'];

interface NavigationProps {
  userRole: UserRole;
}

export function Navigation({ userRole }: NavigationProps) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isSalesLandingRole = salesLandingRoles.includes(userRole);

  // Filter navigation items based on user role, then apply sales landing page consolidation
  const visibleItems = navigationItems
    .filter((item) => item.roles.includes(userRole))
    .filter((item) => {
      // Remove the separate "My Requests" (/holds) tab for sales_ae and sales_support
      if (isSalesLandingRole && item.href === '/holds') {
        return false;
      }
      return true;
    })
    .map((item) => {
      // Rename "Dashboard" to "My Requests" for sales_ae and sales_support
      if (isSalesLandingRole && item.href === '/') {
        return { ...item, label: 'My Requests' };
      }
      return item;
    });

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isMobileMenuOpen]);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <nav className="bg-[#111111] h-14">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-full">
        {/* Mobile hamburger button */}
        <div className="flex items-center h-full md:hidden">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="inline-flex items-center justify-center p-2 rounded-md text-neutral-400 hover:text-white hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-outfront-purple"
            aria-label="Open navigation menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>

        {/* Desktop horizontal navigation */}
        <div className="hidden md:flex space-x-8 h-full">
          {visibleItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'inline-flex items-center px-1 border-b-2 text-sm font-medium transition-colors h-full',
                  isActive
                    ? 'border-outfront-purple text-white'
                    : 'border-transparent text-neutral-400 hover:text-white hover:border-outfront-purple'
                )}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Mobile slide-out drawer */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />

          {/* Drawer panel */}
          <div className="fixed inset-y-0 left-0 w-72 bg-[#111111] flex flex-col">
            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-neutral-800">
              <span className="text-white font-semibold text-base uppercase tracking-[0.05em]">Menu</span>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-md text-neutral-400 hover:text-white hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-outfront-purple"
                aria-label="Close navigation menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Drawer nav links */}
            <div className="flex-1 overflow-y-auto py-2">
              {visibleItems.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      'block px-4 py-3 text-base font-medium transition-colors',
                      isActive
                        ? 'text-white bg-outfront-purple/20 border-l-4 border-outfront-purple'
                        : 'text-neutral-400 hover:text-white hover:bg-white/5 border-l-4 border-transparent'
                    )}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
